import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, Switch, message, Popconfirm, Space, Tag,
  Tooltip, Typography, ColorPicker,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined,
  StopOutlined, CheckCircleOutlined, GlobalOutlined, LoginOutlined,
} from '@ant-design/icons';
import { adminAPI } from '../services/api';

const { Text } = Typography;

// OEM 站点根域名（按当前访问域名动态拼 agent. 二级域名）
function getAgentDashboardUrl() {
  const host = window.location.hostname;
  // 本地开发环境
  if (host === 'localhost' || host === '127.0.0.1') {
    return 'http://localhost:4001';
  }
  // 生产：admin.zjlinghu.com → agent.zjlinghu.com
  const parts = host.split('.');
  if (parts.length >= 3) parts.shift(); // 去掉 admin 等前缀
  return 'https://agent.' + parts.join('.');
}

function getRootDomain() {
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return 'linghusuanli.com';
  const parts = host.split('.');
  if (parts.length >= 3) parts.shift();
  return parts.join('.');
}

const AgentManagement = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getAgents();
      setAgents(res.data?.list || []);
    } catch (error) {
      message.error('获取代理商列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ primary_color: '#1677ff', status: 'active' });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      user_email: record.owner_email,
      agent_name: record.agent_name,
      agent_code: record.agent_code,
      subdomain: record.subdomain,
      logo_url: record.logo_url,
      favicon_url: record.favicon_url || '',
      site_title: record.site_title,
      primary_color: record.primary_color || '#1677ff',
      contact_name: record.contact_name,
      contact_phone: record.contact_phone,
      contact_email: record.contact_email,
      remark: record.remark,
      status: record.status,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await adminAPI.deleteAgent(id);
      message.success('删除成功');
      fetchAgents();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleToggle = async (record) => {
    try {
      await adminAPI.toggleAgent(record.id);
      message.success(record.status === 'active' ? '已禁用' : '已启用');
      fetchAgents();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      // ColorPicker 可能返回对象，统一转为 hex 字符串
      if (values.primary_color && typeof values.primary_color !== 'string') {
        values.primary_color = values.primary_color.toHexString();
      }
      setSubmitting(true);
      if (editing) {
        await adminAPI.updateAgent(editing.id, values);
        message.success('更新成功');
      } else {
        await adminAPI.createAgent(values);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchAgents();
    } catch (error) {
      if (error?.errorFields) return; // 表单校验错误
      message.error(error?.response?.data?.message || (editing ? '更新失败' : '创建失败'));
    } finally {
      setSubmitting(false);
    }
  };

  const copyText = (text, label) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      message.success(`${label}已复制`);
    } catch (err) {
      message.error('复制失败，请手动复制');
    }
    document.body.removeChild(textarea);
  };

  const oemSiteUrl = (subdomain) => {
    const root = getRootDomain();
    return `https://${subdomain}.${root}`;
  };

  const handleImpersonate = async (record) => {
    try {
      const res = await adminAPI.impersonateAgent(record.id);
      const token = res.data?.token;
      const dashUrl = getAgentDashboardUrl() + '/dashboard?agent_token=' + token;
      window.open(dashUrl, '_blank');
    } catch (e) {
      message.error('一键登录失败');
    }
  };

  const columns = [
    {
      title: '代理商', dataIndex: 'agent_name', key: 'agent_name', width: 180,
      render: (text) => (
        <Text strong style={{ whiteSpace: 'nowrap' }}>{text}</Text>
      ),
    },
    {
      title: 'OEM 站点', dataIndex: 'subdomain', key: 'subdomain', width: 240,
      render: (subdomain) => {
        const root = getRootDomain();
        return (
          <Space style={{ whiteSpace: 'nowrap' }}>
            <GlobalOutlined style={{ color: '#1677ff' }} />
            <Text copyable={{ text: oemSiteUrl(subdomain) }}>{subdomain}.{root}</Text>
          </Space>
        );
      },
    },
    {
      title: '绑定账号', dataIndex: 'owner_email', key: 'owner_email', width: 220,
      render: (t) => t
        ? <Text style={{ whiteSpace: 'nowrap' }}>{t}</Text>
        : <Tag>未知</Tag>,
    },
    {
      title: '用户数', dataIndex: 'user_count', key: 'user_count', width: 90,
      align: 'center',
      render: (v) => <Tag color="blue">{v || 0}</Tag>,
      sorter: (a, b) => (a.user_count || 0) - (b.user_count || 0),
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 100,
      align: 'center',
      render: (s) => s === 'active'
        ? <Tag color="green" icon={<CheckCircleOutlined />}>启用</Tag>
        : <Tag color="red" icon={<StopOutlined />}>禁用</Tag>,
    },
    {
      title: '操作', key: 'action', width: 360, fixed: 'right', align: 'center',
      render: (_, record) => (
        <Space size={4} style={{ flexWrap: 'nowrap' }}>
          <Button size="small" type="primary" icon={<LoginOutlined />}
            onClick={() => handleImpersonate(record)}>
            一键登录
          </Button>
          <Tooltip title="复制代理商后台登录地址">
            <Button size="small" icon={<CopyOutlined />}
              onClick={() => copyText(getAgentDashboardUrl(), '后台地址')}>
              后台链接
            </Button>
          </Tooltip>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Button size="small" onClick={() => handleToggle(record)}
            danger={record.status === 'active'}>
            {record.status === 'active' ? '禁用' : '启用'}
          </Button>
          <Popconfirm title="确定删除该代理商？删除后其站点将失效。"
            onConfirm={() => handleDelete(record.id)} okText="删除" cancelText="取消">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>站点管理</Typography.Title>
          <Text type="secondary">管理 OEM 代理商站点，代理商用绑定账号登录 agent.{getRootDomain()} 查看其站数据（只读）</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增代理商</Button>
      </div>

      <Table
        columns={columns}
        dataSource={agents}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1190 }}
        pagination={{ showTotal: (t) => `共 ${t} 个代理商` }}
      />

      <Modal
        title={editing ? '编辑代理商' : '新增代理商'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        confirmLoading={submitting}
        width={600}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="user_email"
            label="绑定账号邮箱"
            rules={[{ required: true, message: '请输入代理商的平台账号邮箱' }]}
            extra="代理商需先在总站注册一个普通账号，此处填该邮箱。该账号即代理商后台登录账号。"
          >
            <Input placeholder="agent@example.com" disabled={!!editing} />
          </Form.Item>

          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="agent_name" label="代理商名称" style={{ flex: 1 }}
              rules={[{ required: true, message: '请输入代理商名称' }]}>
              <Input placeholder="XX科技" />
            </Form.Item>
            <Form.Item name="agent_code" label="代理商码" style={{ flex: 1 }}
              rules={[{ required: true, message: '请输入代理商码' },
                { pattern: /^[a-zA-Z0-9_-]+$/, message: '仅限字母数字、下划线、短横线' }]}
              extra="唯一标识，OEM 前端注册时携带">
              <Input placeholder="agent1" />
            </Form.Item>
          </Space>

          <Form.Item name="subdomain" label="子域名前缀"
            rules={[{ required: true, message: '请输入子域名前缀' },
              { pattern: /^[a-zA-Z0-9-]+$/, message: '仅限字母数字和短横线' }]}
            extra={`用户访问 <前缀>.${getRootDomain()} 即进入该 OEM 站点`}>
            <Input placeholder="agent1" addonAfter={`.${getRootDomain()}`} />
          </Form.Item>

          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="site_title" label="站点标题" style={{ flex: 1 }}
              extra="浏览器标签页标题">
              <Input placeholder="XX科技 - AI聚合平台" />
            </Form.Item>
            <Form.Item name="primary_color" label="主题色">
              <ColorPicker showText />
            </Form.Item>
          </Space>

          <Form.Item name="logo_url" label="Logo URL" extra="留空则使用默认 /logo.png">
            <Input placeholder="/logo.png 或完整图片URL" />
          </Form.Item>

          <Form.Item name="favicon_url" label="Favicon URL" extra="浏览器标签页小图标，留空默认 /favicon.ico">
            <Input placeholder="/favicon.ico 或完整图片URL" />
          </Form.Item>

          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="contact_name" label="联系人" style={{ flex: 1 }}>
              <Input placeholder="张三" />
            </Form.Item>
            <Form.Item name="contact_phone" label="联系电话" style={{ flex: 1 }}>
              <Input placeholder="13800138000" />
            </Form.Item>
          </Space>

          <Form.Item name="contact_email" label="联系邮箱">
            <Input placeholder="contact@example.com" />
          </Form.Item>

          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} placeholder="内部备注（代理商不可见）" />
          </Form.Item>

          <Form.Item name="status" label="状态" valuePropName="checked"
            getValueFromEvent={(checked) => (checked ? 'active' : 'suspended')}
            getValueProps={(v) => ({ checked: v === 'active' })}>
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AgentManagement;
